import axios, { AxiosResponse } from 'axios';
import cryptoJS from 'crypto-js';
import { onError, onSuccess } from 'utils/errors';
import { getStorage } from 'utils/storage';

const { REACT_APP_ANDROMEDA, REACT_APP_SOL } = process.env;

const SOL_SERVICES = {
  SOL: 1,
  GEMINI: 2,
  HYPERION: 3,
  NOVA: 4,
  ORION: 7,
  ANDROMEDA: 8,
};

interface Credentials {
  private_key: string;
  public_key: string;
}

interface LoginPayload {
  email_address: string;
  password: string;
  required_service_ids?: number[];
}

interface PasswordResetPayload {
  reset_code: string;
  password: string;
}

interface PasswordResetPayload {
  reset_code: string;
  password: string;
}

interface RequestPasswordResetPayload {
  email_address: string;
}

class Axios {
  private_key = '';
  public_key = '';
  host = '';

  constructor(host: string) {
    const storage = getStorage();
    this.private_key = storage.private_key || '';
    this.public_key = storage.public_key || '';
    this.host = host;
  }

  persistKeys({ private_key, public_key }: Credentials) {
    this.private_key = private_key || '';
    this.public_key = public_key || '';
  }

  generateHeaders(url: string) {
    const hash = cryptoJS.HmacSHA256(url, this.private_key).toString().replace('-', '').toLowerCase();
    return {
      'Content-Type': 'application/json',
      public_key: this.public_key,
      hash,
    };
  }

  async login(payload: LoginPayload) {
    payload.required_service_ids = [SOL_SERVICES.ANDROMEDA];

    const res: AxiosResponse['data'] = await this.auth('/authentication/login', payload);

    this.private_key = res.data.private_key;
    this.public_key = res.data.public_key;

    return res;
  }

  async passwordReset(payload: PasswordResetPayload) {
    const { password } = payload;
    const res: AxiosResponse['data'] = await this.auth(`/authentication/reset_password/${payload.reset_code}`, {
      password,
    });
    return res;
  }

  async requestPasswordReset(payload: RequestPasswordResetPayload) {
    const res: AxiosResponse['data'] = await this.auth('/authentication/reset_password', payload);
    return res;
  }

  async auth(path: string, payload = {}, mock?: any) {
    return new Promise((resolve, reject) => {
      axios({
        method: 'post',
        headers: { 'Content-Type': 'application/json' },
        url: `${this.host}${path}`,
        data: JSON.stringify(payload),
      })
        .then((res) => onSuccess(res, resolve, resolve, mock))
        .catch((err) => onError(err, reject));
    });
  }

  async get(path: string, mock?: any) {
    const paths = path.split('?');
    const [url] = paths;
    const headers = this.generateHeaders(url || '');

    return new Promise((resolve, reject) => {
      axios({
        method: 'get',
        url: `${this.host}${paths.join('?')}`,
        headers,
      })
        .then((res) => onSuccess(res, resolve, reject, mock))
        .catch((err) => onError(err, reject));
    });
  }

  async post(path: string, payload = {}, mock?: any) {
    const paths = path.split('?');
    const [url] = paths;
    const string = JSON.stringify(payload);
    const headers = this.generateHeaders(`${url || ''}${string}`);
    return new Promise((resolve, reject) => {
      axios({
        method: 'post',
        headers,
        url: `${this.host}${paths.join('?')}`,
        data: payload,
      })
        .then((res) => onSuccess(res, resolve, reject, mock))
        .catch((err) => onError(err, reject));
    });
  }
}

export const Andromeda = new Axios(REACT_APP_ANDROMEDA || '');
export const Sol = new Axios(REACT_APP_SOL || '');
